/**
 * Short-lived sessionStorage model for auth-gated event actions (RSVP, timeline, etc.).
 * Survives Clerk sign-in redirect; validated before replay on the event page.
 */

import type { PersistedRsvpStatus } from "@/lib/events/types"
import { validateInternalReturnPath } from "@/lib/auth/safe-internal-redirect"

export const PENDING_EVENT_ACTION_STORAGE_KEY =
  "maatii:pending-event-action:v2" as const

const REPLAY_NONCE_INDEX_KEY = "maatii:pending-event-replay-nonces:v1" as const

/** Align with auth intent / session return TTL in safe-internal-redirect. */
export const PENDING_EVENT_ACTION_TTL_MS = 15 * 60 * 1000

/** How long we remember a completed nonce to block duplicate refresh/replay. */
const NONCE_DONE_TTL_MS = 60 * 60 * 1000

const SCHEMA_VERSION = 2 as const

export type PendingEventActionKind =
  | "timeline_save"
  | "event_rsvp"
  | "event_attend"

export type PendingEventActionV2 = {
  v: typeof SCHEMA_VERSION
  kind: PendingEventActionKind
  eventId: string
  /** Validated internal path (path + allowed query), from validateInternalReturnPath */
  returnPath: string
  nonce: string
  createdAtMs: number
  rsvpStatus?: PersistedRsvpStatus
  /** Redundant hint for validation; must match token embedded in returnPath when present */
  shareToken?: string | null
}

function pruneNonceMap(map: Record<string, number>): Record<string, number> {
  const now = Date.now()
  const next: Record<string, number> = {}
  for (const [k, t] of Object.entries(map)) {
    if (now - t <= NONCE_DONE_TTL_MS) next[k] = t
  }
  return next
}

function readNonceMap(): Record<string, number> {
  if (typeof window === "undefined") return {}
  try {
    const raw = sessionStorage.getItem(REPLAY_NONCE_INDEX_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== "object") return {}
    return pruneNonceMap(parsed as Record<string, number>)
  } catch {
    return {}
  }
}

function writeNonceMap(map: Record<string, number>): void {
  if (typeof window === "undefined") return
  try {
    sessionStorage.setItem(REPLAY_NONCE_INDEX_KEY, JSON.stringify(map))
  } catch {
    /* ignore */
  }
}

/** Returns true if this nonce already completed a successful replay (guards refresh/back). */
export function isPendingReplayNonceDone(nonce: string): boolean {
  const id = nonce.trim()
  if (!id) return false
  const map = readNonceMap()
  const t = map[id]
  if (t == null) return false
  if (Date.now() - t > NONCE_DONE_TTL_MS) {
    const next = { ...map }
    delete next[id]
    writeNonceMap(pruneNonceMap(next))
    return false
  }
  return true
}

export function markPendingReplayNonceDone(nonce: string): void {
  const id = nonce.trim()
  if (!id) return
  const map = pruneNonceMap(readNonceMap())
  map[id] = Date.now()
  writeNonceMap(map)
}

export function clearStalePendingEventActions(): void {
  if (typeof window === "undefined") return
  try {
    const raw = sessionStorage.getItem(PENDING_EVENT_ACTION_STORAGE_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw) as Partial<PendingEventActionV2>
    if (
      typeof parsed.createdAtMs !== "number" ||
      Date.now() - parsed.createdAtMs > PENDING_EVENT_ACTION_TTL_MS
    ) {
      sessionStorage.removeItem(PENDING_EVENT_ACTION_STORAGE_KEY)
    }
  } catch {
    sessionStorage.removeItem(PENDING_EVENT_ACTION_STORAGE_KEY)
  }
}

export function clearPendingEventAction(): void {
  if (typeof window === "undefined") return
  try {
    sessionStorage.removeItem(PENDING_EVENT_ACTION_STORAGE_KEY)
  } catch {
    /* ignore */
  }
}

function parsePayload(raw: string): PendingEventActionV2 | null {
  try {
    const parsed = JSON.parse(raw) as Partial<PendingEventActionV2>
    if (parsed.v !== SCHEMA_VERSION) return null
    if (parsed.kind !== "timeline_save" && parsed.kind !== "event_rsvp" && parsed.kind !== "event_attend") {
      return null
    }
    const eventId = typeof parsed.eventId === "string" ? parsed.eventId.trim() : ""
    const returnPath =
      typeof parsed.returnPath === "string" ? parsed.returnPath.trim() : ""
    const nonce = typeof parsed.nonce === "string" ? parsed.nonce.trim() : ""
    if (!eventId || !returnPath || !nonce) return null
    if (typeof parsed.createdAtMs !== "number" || !Number.isFinite(parsed.createdAtMs)) {
      return null
    }
    const validatedPath = validateInternalReturnPath(returnPath)
    if (!validatedPath) return null
    const out: PendingEventActionV2 = {
      v: SCHEMA_VERSION,
      kind: parsed.kind,
      eventId,
      returnPath: validatedPath,
      nonce,
      createdAtMs: parsed.createdAtMs,
      shareToken:
        parsed.shareToken === null || parsed.shareToken === undefined
          ? null
          : String(parsed.shareToken).trim() || null,
    }
    if (parsed.kind === "event_rsvp") {
      const s = parsed.rsvpStatus
      if (s !== "going" && s !== "maybe" && s !== "declined") return null
      out.rsvpStatus = s
    }
    return out
  } catch {
    return null
  }
}

export function readPendingEventAction(): PendingEventActionV2 | null {
  if (typeof window === "undefined") return null
  try {
    const raw = sessionStorage.getItem(PENDING_EVENT_ACTION_STORAGE_KEY)
    if (!raw) return null
    const parsed = parsePayload(raw)
    if (!parsed) {
      sessionStorage.removeItem(PENDING_EVENT_ACTION_STORAGE_KEY)
      return null
    }
    if (Date.now() - parsed.createdAtMs > PENDING_EVENT_ACTION_TTL_MS) {
      sessionStorage.removeItem(PENDING_EVENT_ACTION_STORAGE_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

/**
 * Current browser location as a validated internal path, or null if invalid.
 */
export function getValidatedLocationPath(): string | null {
  if (typeof window === "undefined") return null
  return validateInternalReturnPath(
    `${window.location.pathname}${window.location.search}`,
  )
}

/**
 * Returns pending action if it matches this event and the current validated URL.
 */
export function getPendingActionForCurrentPage(
  eventId: string,
): PendingEventActionV2 | null {
  clearStalePendingEventActions()
  const pending = readPendingEventAction()
  if (!pending) return null
  const eid = eventId.trim()
  if (!eid || pending.eventId !== eid) return null

  if (isPendingReplayNonceDone(pending.nonce)) {
    clearPendingEventAction()
    return null
  }

  const here = getValidatedLocationPath()
  if (!here || here !== pending.returnPath) return null

  const t = pending.shareToken?.trim() ?? ""
  if (t) {
    const hereUrl = new URL(`http://local.invalid${here}`)
    const shareHere = hereUrl.searchParams.get("share")?.trim() ?? ""
    if (shareHere !== t) return null
  }

  return pending
}

export type WritePendingInput = {
  kind: PendingEventActionKind
  eventId: string
  /** Raw path+query; will be validated before persisting */
  returnPathRaw: string
  rsvpStatus?: PersistedRsvpStatus
  shareToken?: string | null
}

/**
 * Persists pending intent and returns the validated return path for sign-in/up `redirect_url`.
 */
export function writePendingEventAction(input: WritePendingInput): {
  returnPath: string
  payload: PendingEventActionV2
} {
  const validated = validateInternalReturnPath(input.returnPathRaw)
  if (!validated) {
    throw new Error("Invalid return path for pending event action")
  }
  const eventId = input.eventId.trim()
  if (!eventId) throw new Error("Missing event id")

  const nonce =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `n-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`

  const payload: PendingEventActionV2 = {
    v: SCHEMA_VERSION,
    kind: input.kind,
    eventId,
    returnPath: validated,
    nonce,
    createdAtMs: Date.now(),
    shareToken: input.shareToken?.trim() ? input.shareToken.trim() : null,
  }
  if (input.kind === "event_rsvp") {
    const s = input.rsvpStatus
    if (s !== "going" && s !== "maybe" && s !== "declined") {
      throw new Error("RSVP status required for event_rsvp")
    }
    payload.rsvpStatus = s
  }
  if (typeof window === "undefined") {
    return { returnPath: validated, payload }
  }
  try {
    sessionStorage.setItem(PENDING_EVENT_ACTION_STORAGE_KEY, JSON.stringify(payload))
  } catch {
    /* quota / private mode */
  }
  return { returnPath: validated, payload }
}

/** After a successful automatic replay: block duplicate execution and clear storage. */
export function consumePendingActionAfterSuccess(nonce: string): void {
  markPendingReplayNonceDone(nonce)
  clearPendingEventAction()
}
